import { useContext, useState, useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Link from '@mui/material/Link';
import { useTranslation } from 'react-i18next';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { AppContext } from '../sporttia/all';
import FooterMenuItem from './FooterMenuItem';
import { getPath } from '../pages/Pages';
import WelcomeDialog from '../components/dialogs/WelcomeDialog';
import { useMenusTour } from '../lib/tours/useMenusTour';
import logoEUNextGen from '../images/dipSevillaLogos/logoEUNextGeneration.png';
import logoPlanRecuperacion from '../images/dipSevillaLogos/logoPlanRecuperacion.png';
import logoDipuSevilla from '../images/dipSevillaLogos/logoDipuSevilla.png';
import logoGob from '../images/dipSevillaLogos/MPTMD.Gob.png';

export default function Footer(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [showWelcome, setShowWelcome] = useState<boolean>(false);
    const { startTour } = useMenusTour();
    const prevZeroRef = useRef<boolean | undefined>(undefined);

    // Open welcome popup when zero changes from false to true
    useEffect(() => {
        if (prevZeroRef.current === false && cxt.zero === true) {
            setShowWelcome(true);
        }
        prevZeroRef.current = cxt.zero;
    }, [cxt.zero]);

    function goContactPage(): void {
        navigate(getPath('contact'));
    }

    // -----| Render |-----
    return (
        <Box
            component="footer"
            sx={{
                mt: 'auto',
                py: 3,
                px: 3,
                backgroundColor: 'transparent',
                position: 'relative',
            }}
        >
            {/* Subscription button - bottom left when zero mode is active */}
            {cxt.zero && (
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SubscriptionsIcon />}
                    onClick={() => navigate(getPath('subscriptions'))}
                    sx={{
                        position: 'fixed',
                        // Por encima de la barra inferior de móvil, que reserva
                        // su alto en --bottom-nav-height (0 desde md).
                        bottom: 'calc(16px + var(--bottom-nav-height))',
                        left: 16,
                        zIndex: 1000,
                    }}
                >
                    {t('Subscription')}
                </Button>
            )}
            <Box
                sx={{
                    maxWidth: 1200,
                    mx: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {/* Centered content row with all items */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    {/* El idioma se elige en los ajustes del sidebar. */}
                    {(!cxt?.logged ||
                        (cxt?.logged && cxt?.user?.role === 'ADMIN')) && (
                        <FooterMenuItem
                            caption={t('contact.title')}
                            onClick={goContactPage}
                        />
                    )}
                    <Link
                        href="https://www.sporttia.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: 'var(--brand-text)',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        <Typography variant="caption">
                            {t('contact.legalWarning')}
                        </Typography>
                    </Link>
                    <Link
                        href="https://sporttia.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: 'var(--brand-text)',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        <Typography variant="caption">
                            {t('contact.privacyPolicy')}
                        </Typography>
                    </Link>
                    <Link
                        href="https://sporttia.com/cookies"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: 'var(--brand-text)',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        <Typography variant="caption">
                            {t('contact.cookiesPolicy')}
                        </Typography>
                    </Link>
                    <Link
                        href="https://sporttia.com/contracting-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: 'var(--brand-text)',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        <Typography variant="caption">
                            {t('contact.contractingConditions')}
                        </Typography>
                    </Link>
                </Box>

                {/* Copyright row */}
                <Box sx={{ textAlign: 'center' }}>
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                    >
                        © {new Date().getFullYear()} Sporttia (
                        {cxt?.config?.version} - {cxt?.backendVersion}). All
                        rights reserved
                    </Typography>
                </Box>

                {/* Diputación de Sevilla logos */}
                {Boolean(cxt?.sc?.isDipuSevilla) && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                        }}
                    >
                        <img
                            src={logoEUNextGen}
                            alt="Logo EU Next Generation"
                            width="120px"
                            height="31px"
                        />
                        <img
                            src={logoGob}
                            alt="Logo Gobierno de España Min. Transp, Mov, Ag Urb"
                            width="120px"
                            height="31px"
                        />
                        <img
                            src={logoPlanRecuperacion}
                            alt="Logo Plan de recuperación"
                            width="120px"
                            height="60px"
                        />
                        <img
                            src={logoDipuSevilla}
                            alt="Logo Diputación de Sevilla"
                            width="31px"
                            height="31px"
                        />
                    </Box>
                )}
            </Box>

            <WelcomeDialog
                open={showWelcome}
                onClose={() => setShowWelcome(false)}
                onStartTour={startTour}
            />
        </Box>
    );
}
